import { Link } from 'react-router-dom'
import type { Container } from '@/api/types'
import StatusBadge from './StatusBadge'
import { Badge } from '@/admin-design/_common'

interface ContainerRowProps {
  container: Container
  nodeId: number
  /** Override the default /containers/... link — used by Dashboard to
   *  route registered services to their ServiceDetail page instead of
   *  the generic ContainerDetail view. */
  linkTo?: string
}

/** Truncate image name: keep last segment and tag */
function shortImage(image: string): string {
  const parts = image.split('/')
  const last = parts[parts.length - 1]
  return last.length > 40 ? last.slice(0, 37) + '...' : last
}

/** Extract version/hash tag from image (e.g. "ghcr.io/org/app:abc1234" → "abc1234") */
function imageTag(image: string): string {
  const idx = image.lastIndexOf(':')
  if (idx < 0) return ''
  const tag = image.slice(idx + 1)
  return tag === 'latest' ? '' : tag
}

/** Format container name: strip leading slash */
function shortName(names: string[]): string {
  const name = names[0] ?? ''
  return name.startsWith('/') ? name.slice(1) : name
}

/** Format port mapping */
function formatPorts(container: Container): string {
  if (!container.Ports || container.Ports.length === 0) return ''
  return container.Ports.filter((p) => p.PublicPort)
    .map((p) => `${p.PublicPort}:${p.PrivatePort}`)
    .join(', ')
}

export default function ContainerRow({
  container,
  nodeId,
  linkTo,
}: ContainerRowProps) {
  const name = shortName(container.Names)
  const ports = formatPorts(container)
  const href = linkTo ?? `/containers/${nodeId}/${container.Id}`

  return (
    <Link
      to={href}
      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted/50 transition-colors group"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-sm text-card-foreground truncate">
            {name}
          </span>
          <StatusBadge state={container.State} />
        </div>
        <div className="flex items-center gap-3 mt-0.5">
          <span className="text-xs text-muted-foreground truncate">
            {shortImage(container.Image)}
          </span>
          {imageTag(container.Image) && (
            <Badge variant="info" className="font-mono text-xs rounded px-1 py-0">
              {imageTag(container.Image)}
            </Badge>
          )}
          {ports && (
            <span className="text-xs text-muted-foreground">{ports}</span>
          )}
        </div>
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:block">
        {container.Status}
      </span>
    </Link>
  )
}
